import {
  IProfitCalculator,
  TFeeRules,
} from "../features/productAnalyzer/productAnalyzer.types";
import { toNumber, toPercent, toPrice } from "./formatter";

interface ICalculateProfitRes {
  totalFee: number;
  costSubTotal: number;
  profit: number;
  roi: number;
  margin: number;
  payout: number;
  maxCost: number;
  referralFee: number;
  referralPercent: number;
  referralHint?: string;
  fbaFee: number;
  storageFee: number;
  inboundPlacementFee: number;
  // vatPercentageAmount: number;
  // vatAmount: number;
  // vatTotal: number;
  prepFee: number;
  miscFeeAmount: number;
  miscFeePercentAmount: number;
  breakevenSalePrice: number;
  packageCost: number;
  discount: number;
}

export class ProfitCalculator {
  data: IProfitCalculator;
  feeRules: TFeeRules;
  salePrice: number;
  totalCost: number;
  discount: number;
  multipackQuantity: number;

  constructor(data: IProfitCalculator, feeRules: TFeeRules) {
    this.data = data;
    this.feeRules = feeRules;
    this.salePrice = toNumber(data.salePrice);
    this.multipackQuantity = toNumber(data.multipackQuantity) || 1;
    this.totalCost = toNumber(data.costPrice) * this.multipackQuantity;
    this.discount = (this.totalCost * toNumber(data.totalDiscount)) / 100;
  }

  calculate(): ICalculateProfitRes {
    const totalFee = this.calculateTotalFees();
    const costSubTotal = this.calculateCostSubTotal();
    const profit = this.calculateProfit();
    const payout = this.calculateAmazonPayout();
    const roi = this.calculateRoi();
    const margin = this.calculateProfitMargin();
    const maxCost = this.calculateMaxCost();
    const { referralFee, referralPercent, referralHint } =
      this.calculateReferralFee();
    const fbaFee = this.calculateFbaFee();
    const storageFee = this.calculateStorageFee();
    const inboundPlacementFee = this.calculateInboundPlacementFee();
    const miscFeePercentAmount = this.calculateMiscPercentAmount();
    // const vatPercentageAmount = this.calculateVatPercentAmount();
    // const vatAmount = this.getVatAmount();
    const prepFee = this.getPrepFee();
    const miscFeeAmount = this.getMiscFeeAmount();
    const breakevenSalePrice = this.calculateBreakevenSalePrie();

    return {
      totalFee,
      costSubTotal,
      packageCost: this.totalCost,
      profit,
      roi,
      margin,
      payout,
      maxCost,
      referralFee,
      referralPercent,
      referralHint,
      fbaFee,
      storageFee,
      inboundPlacementFee,
      miscFeePercentAmount,
      // vatPercentageAmount,
      // vatAmount,
      // vatTotal: vatPercentageAmount + vatAmount,
      prepFee,
      miscFeeAmount,
      breakevenSalePrice,
      discount: this.discount,
    };
  }

  getMinRoi(): number {
    return toNumber(this.data.minRoi) / 100;
  }

  getMinProfit(): number {
    return toNumber(this.data.minProfit);
  }

  getMiscPercent(): number {
    return toNumber(this.data.miscFeePercent);
  }

  getDiscountPercent(): number {
    return toNumber(this.data.totalDiscount);
  }

  calculateMaxCost(): number {
    const miscPercentAmount = this.calculateMiscPercentAmount();
    const miscPercent = this.getMiscPercent() / 100;
    const discount = this.getDiscountPercent() / 100;
    const totalFeeWithoutMisc = this.calculateTotalFees() - miscPercentAmount;
    const maxCost =
      (this.salePrice - totalFeeWithoutMisc) /
      ((1 + miscPercent - discount) * this.multipackQuantity);
    const minRoi = this.getMinRoi();
    const minProfit = this.getMinProfit();
    const mins: number[] = [maxCost];
    const cond1 =
      (minRoi - (minRoi + 1) * discount + 1 + miscPercent) *
      this.multipackQuantity;
    const cond2 = this.multipackQuantity - this.multipackQuantity * discount;
    const cost1 = (this.salePrice - totalFeeWithoutMisc) / cond1;
    if ((cond2 > 0 && cond1 > 0) || (cond2 < 0 && cond1 < 0)) {
      mins.push(cost1);
    }
    const cond3 = (1 + miscPercent - discount) * this.multipackQuantity;
    const cost2 = (this.salePrice - totalFeeWithoutMisc - minProfit) / cond3;
    if (cond3 > 0) {
      mins.push(cost2);
    }

    return toPrice(Math.min(...mins), false);
  }

  calculateProfitMargin(): number {
    return toPrice((this.calculateProfit() / this.salePrice) * 100);
  }

  calculateRoi(): number {
    const roi =
      (this.calculateProfit() / (this.totalCost - this.discount)) * 100;
    return toPercent(roi);
  }

  calculateProfitFromRoi(roi: number): number {
    return toPrice((roi / 100) * (this.totalCost - this.discount), false);
  }

  calculateSalePriceFromRoi(roi: number): number | undefined {
    const amount: number =
      (roi / 100) * (this.totalCost - this.discount) +
      this.totalCost +
      this.calculateOtherFees() -
      this.discount;
    return this.calculateSalePrice(amount);
  }

  calculateSalePrice(amount: number): number | undefined {
    const { referralFeeRule, fbaFeeRule } = this.feeRules;
    let fbaFees = [];
    if (fbaFeeRule && fbaFeeRule.length) {
      fbaFees = fbaFeeRule.map((rule) => toPrice(rule.rate));
    } else {
      fbaFees = [0];
    }
    let referralFeeType;
    let rulesArr: string[] = [];

    if (referralFeeRule) {
      rulesArr = referralFeeRule.split("-");
    }

    if (rulesArr.length > 0) {
      referralFeeType = rulesArr[0];
    }

    if (referralFeeType === "FIXED") {
      const referralPercent = +rulesArr[1];
      for (let fbaKey in fbaFees) {
        const fbaFee = fbaFees[fbaKey];
        const salePrice = (amount + fbaFee) / (1 - referralPercent / 100);
        if (this.calculateFbaFee(salePrice) === fbaFee) {
          return toPrice(salePrice, false);
        }
      }
    }

    if (referralFeeType === "VARIABLE") {
      const referralPercents = [+rulesArr[1], +rulesArr[2]];
      for (let fbaKey in fbaFees) {
        const fbaFee = fbaFees[fbaKey];
        for (let referralKey in referralPercents) {
          const referralPercent = referralPercents[referralKey];
          const salePrice = (amount + fbaFee) / (1 - referralPercent / 100);
          if (
            this.calculateFbaFee(salePrice) === fbaFee &&
            ((salePrice <= +rulesArr[3] && referralPercent === +rulesArr[1]) ||
              (salePrice > +rulesArr[3] && referralPercent === +rulesArr[2]))
          ) {
            return toPrice(salePrice, false);
          }
        }
      }
    }

    if (referralFeeType === "MULTIVAR") {
      const referralPercents = [+rulesArr[1], +rulesArr[2], +rulesArr[3]];
      for (let fbaKey in fbaFees) {
        const fbaFee = fbaFees[fbaKey];
        for (let referralKey in referralPercents) {
          const referralPercent = referralPercents[referralKey];
          const salePrice = (amount + fbaFee) / (1 - referralPercent / 100);
          if (
            this.calculateFbaFee(salePrice) === fbaFee &&
            ((salePrice <= +rulesArr[4] && referralPercent === +rulesArr[1]) ||
              (salePrice > +rulesArr[4] &&
                salePrice <= +rulesArr[5] &&
                referralPercent === +rulesArr[2]) ||
              (salePrice > +rulesArr[5] && referralPercent === +rulesArr[3]))
          ) {
            return toPrice(salePrice, false);
          }
        }
      }
    }

    if (referralFeeType === "TIERED") {
      for (let fbaKey in fbaFees) {
        const fbaFee = fbaFees[fbaKey];
        let salePrice = (amount + fbaFee) / (1 - +rulesArr[1] / 100);
        if (
          this.calculateFbaFee(salePrice) === fbaFee &&
          salePrice <= +rulesArr[3]
        ) {
          return toPrice(salePrice, false);
        }

        salePrice =
          (amount +
            fbaFee +
            (+rulesArr[3] * +rulesArr[1]) / 100 -
            (+rulesArr[3] * +rulesArr[2]) / 100) /
          (1 - +rulesArr[2] / 100);
        if (
          this.calculateFbaFee(salePrice) === fbaFee &&
          salePrice > +rulesArr[3]
        ) {
          return toPrice(salePrice, false);
        }
      }
    }

    if (referralFeeType === "MULTITIERED") {
      for (let fbaKey in fbaFees) {
        const fbaFee = fbaFees[fbaKey];
        let salePrice = (amount + fbaFee) / (1 - +rulesArr[1] / 100);
        if (
          this.calculateFbaFee(salePrice) === fbaFee &&
          salePrice <= +rulesArr[4]
        ) {
          return toPrice(salePrice);
        }

        salePrice =
          (amount +
            fbaFee +
            (+rulesArr[4] * +rulesArr[1]) / 100 -
            (+rulesArr[4] * +rulesArr[2]) / 100) /
          (1 - +rulesArr[2] / 100);
        if (
          this.calculateFbaFee(salePrice) === fbaFee &&
          salePrice > +rulesArr[4] &&
          salePrice <= +rulesArr[5]
        ) {
          return toPrice(salePrice, false);
        }

        salePrice =
          (amount +
            fbaFee +
            (+rulesArr[4] * +rulesArr[1]) / 100 +
            (+rulesArr[5] * +rulesArr[2]) / 100 -
            ((+rulesArr[4] + +rulesArr[5]) * +rulesArr[3]) / 100) /
          (1 - +rulesArr[3] / 100);
        if (
          this.calculateFbaFee(salePrice) === fbaFee &&
          salePrice > +rulesArr[5]
        ) {
          return toPrice(salePrice, false);
        }
      }
    }

    if (!referralFeeType) {
      for (let fbaKey in fbaFees) {
        const fbaFee = fbaFees[fbaKey];
        const salePrice = amount + fbaFee;
        if (this.calculateFbaFee(salePrice) === fbaFee) {
          return toPrice(salePrice, false);
        }
      }
    }
  }

  calculateRoiFromProfit(profit: number) {
    const roi = (100 * profit) / (this.totalCost - this.discount);
    return toPercent(roi);
  }

  calculateSalePriceFromProfit(profit: number): number | undefined {
    const amount: number =
      profit + this.totalCost + this.calculateOtherFees() - this.discount;
    return this.calculateSalePrice(amount);
  }

  calculateProfit(): number {
    return toPrice(
      this.salePrice - this.calculateCostSubTotal() + this.discount,
      false,
    );
  }

  // getVatAmount(): number {
  //   return toPrice(this.data.vatAmount);
  // }

  getPrepFee(): number {
    return toPrice(this.data.prepFee);
  }

  getMiscFeeAmount(): number {
    return toPrice(this.data.miscFee);
  }

  calculateCostSubTotal(): number {
    return this.totalCost + this.calculateTotalFees();
  }

  calculateReferralFee(salePrice = this.salePrice): {
    referralFee: number;
    referralPercent: number;
    referralHint?: string;
  } {
    const { referralFeeRule } = this.feeRules;
    let referralPercent = 0;
    let referralFee = 0;
    let referralHint;
    if (referralFeeRule) {
      const rulesArr = referralFeeRule.split("-");
      if (rulesArr.length > 0) {
        referralPercent = +rulesArr[1];
        switch (rulesArr[0]) {
          case "FIXED":
            referralFee = (salePrice * referralPercent) / 100;
            referralHint = referralPercent.toFixed(2) + "%";
            break;
          case "VARIABLE":
            if (rulesArr.length < 4)
              throw new Error("Invalid referral rule for variable");
            referralPercent =
              salePrice <= +rulesArr[3] ? +rulesArr[1] : +rulesArr[2];
            referralFee = (salePrice * referralPercent) / 100;
            referralHint =
              "<strong>Variable Referral Fee</strong><br>" +
              "Sale Price up to $" +
              rulesArr[3] +
              ": " +
              (+rulesArr[1]).toFixed(2) +
              "% <br>" +
              "Sale Price above $" +
              rulesArr[3] +
              ": " +
              (+rulesArr[2]).toFixed(2) +
              "%";
            break;
          case "MULTIVAR":
            if (rulesArr.length < 6)
              throw new Error("Invalid referral rule for multivar");
            referralPercent =
              salePrice <= +rulesArr[4]
                ? +rulesArr[1]
                : salePrice <= +rulesArr[5]
                  ? +rulesArr[2]
                  : +rulesArr[3];
            referralFee = (salePrice * referralPercent) / 100;
            referralHint =
              "<strong>Variable Referral Fee</strong><br>" +
              "Sale Price up to $" +
              rulesArr[4] +
              ": " +
              (+rulesArr[1]).toFixed(2) +
              "% <br>" +
              "Sale Price greater than $" +
              rulesArr[4] +
              " and less than or equal to $" +
              rulesArr[5] +
              ": " +
              (+rulesArr[2]).toFixed(2) +
              "% <br>" +
              "Sale Price greater than $" +
              rulesArr[5] +
              ": " +
              (+rulesArr[3]).toFixed(2) +
              "%";
            break;
          case "TIERED":
            if (rulesArr.length < 4)
              throw new Error("Invalid referral rule for tiered");
            const firstPart = Math.min(salePrice, +rulesArr[3]);
            referralFee = (firstPart * +rulesArr[1]) / 100;
            referralHint =
              "<strong>Tiered Referral Fee</strong><br>$" +
              firstPart.toFixed(2) +
              " at " +
              (+rulesArr[1]).toFixed(2) +
              "%";
            if (salePrice > +rulesArr[3]) {
              const secondPart = salePrice - +rulesArr[3];
              referralFee += (secondPart * +rulesArr[2]) / 100;
              referralHint =
                referralHint +
                "<br> plus $" +
                secondPart.toFixed(2) +
                " at " +
                (+rulesArr[2]).toFixed(2) +
                "% ";
            }
            break;
          case "MULTITIERED":
            if (rulesArr.length < 6)
              throw new Error("Invalid referral rule for multi-tiered");
            const firstPortion = Math.min(salePrice, +rulesArr[4]);
            referralFee = (firstPortion * +rulesArr[1]) / 100;
            referralHint =
              "<strong>Tiered Referral Fee</strong><br>$" +
              firstPortion.toFixed(2) +
              " at " +
              (+rulesArr[1]).toFixed(2) +
              "%";
            if (salePrice > +rulesArr[4]) {
              const secondPortion =
                Math.min(salePrice, +rulesArr[5]) - +rulesArr[4];
              referralFee += (secondPortion * +rulesArr[2]) / 100;
              referralHint =
                referralHint +
                "<br> plus $" +
                secondPortion.toFixed(2) +
                " at " +
                (+rulesArr[2]).toFixed(2) +
                "% ";
            }
            if (salePrice > +rulesArr[5]) {
              const thirdPortion = salePrice - +rulesArr[5];
              referralFee += (thirdPortion * +rulesArr[3]) / 100;
              referralHint =
                referralHint +
                "<br> plus $" +
                thirdPortion.toFixed(2) +
                " at " +
                (+rulesArr[3]).toFixed(2) +
                "% ";
            }
            break;
        }
      }
    }

    return { referralFee: toPrice(referralFee), referralPercent, referralHint };
  }

  calculateFbaFee(salePrice = this.salePrice): number {
    const { fbaFeeRule } = this.feeRules;
    for (let rule of fbaFeeRule) {
      const { min, max, rate } = rule;
      if (min <= salePrice && (max === null || salePrice <= max)) {
        return toPrice(rate);
      }
    }
    return 0;
  }

  calculateAmazonPayout(): number {
    const { closingFee } = this.data;
    const { referralFee } = this.calculateReferralFee();
    return toPrice(
      this.salePrice -
        referralFee -
        this.calculateFbaFee() -
        toNumber(closingFee),
    );
  }

  calculateOtherFees(): number {
    const miscFeeAmount = this.getMiscFeeAmount();
    const miscFeePercentAmount = this.calculateMiscPercentAmount();
    const closingFee = toNumber(this.data.closingFee);
    // const vatPercentageAmount = this.calculateVatPercentAmount();
    // const vatAmount = this.getVatAmount();
    // const vatTotal = vatPercentageAmount + vatAmount;
    let total =
      closingFee +
      miscFeeAmount +
      // vatTotal +
      miscFeePercentAmount;
    if (this.data.fulfilmentType === "FBA") {
      const prepFee = this.getPrepFee();
      const storageFee = this.calculateStorageFee();
      const inboundPlacementFee = this.calculateInboundPlacementFee();
      const inboundShipping = toNumber(this.data.inboundShipping);
      total += storageFee + prepFee + inboundPlacementFee + inboundShipping;
    }
    return total;
  }

  calculateTotalFees(): number {
    const { referralFee } = this.calculateReferralFee();
    const fbaFee = this.calculateFbaFee();
    const fbmCost = toNumber(this.data.fbmCost);
    const fulfillmentFee =
      this.data.fulfilmentType === "FBA" ? fbaFee : fbmCost;
    return this.calculateOtherFees() + referralFee + fulfillmentFee;
  }

  calculateInboundPlacementFee(): number {
    return toPrice(
      this.feeRules.inboundPlacementServiceFeeRule.find(
        (rule) => rule.name === this.data.inboundPlacementService,
      )?.fee,
    );
  }

  calculateStorageFee(): number {
    return toPrice(
      toNumber(this.data.storageFee) * toNumber(this.data.storageTime),
    );
  }

  // calculateVatPercentAmount(): number {
  //   if (isNumber(this.settings.vatPercentage)) {
  //     return (
  //       toPrice(this.salePrice * toNumber(this.settings.vatPercentage)) / 100
  //     );
  //   } else {
  //     return toPrice(this.data.vatPercentageAmount);
  //   }
  // }

  calculateMiscPercentAmount(): number {
    return toPrice((this.totalCost * toNumber(this.data.miscFeePercent)) / 100);
  }

  calculateBreakevenSalePrie() {
    const totalFee = this.calculateTotalFees();
    const { referralFee, referralPercent } = this.calculateReferralFee();
    return toPrice(
      (totalFee - referralFee + this.totalCost - this.discount) /
        (1 - referralPercent / 100),
    );
  }

  updatePriceAndCalculate(value: any) {
    this.salePrice = toNumber(value);
    return this.calculate();
  }
}

export default ProfitCalculator;
